import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, mapTo, tap } from 'rxjs/operators';
import {
    ComponentSettingsKey,
    GlobalSettings,
    GlobalSettingsKey,
    GlobalValues,
    RouteSettingsKey,
    Settings,
    SettingsKey,
    SettingsScope,
    SettingsValue
} from './user-setting.model';
import {
    SettingsUIState,
    GlobalUIState,
    UserSettingsStore,
    SettingsUI,
    SettingsUIStatus
} from './user-settings.store';
import { Router } from '@angular/router';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { applyTransaction, snapshotManager } from '@datorama/akita';
import { Observable, of, throwError } from 'rxjs';
import { ToastService } from '../../../shared/services/toast/toast.service';
import { DEFAULT_SETTINGS } from './user-setting.default';
import { SbEventBusService, SbEventTopic } from '@services';
import { deepEquals } from '@utils';

interface ConfigItem {
    configName: string;
    config: any;
}

interface UserSettingsSnapshot {
    entities: {};
    ids: any[];
    loading: boolean;
    error: null;
}

interface Snapshots {
    'user-settings-route': UserSettingsSnapshot;
    'user-settings-component': UserSettingsSnapshot;
    'user-settings-global': {};
}

@Injectable({ providedIn: 'root' })
export class UserSettingsService {
    constructor(
        private userSettingsStore: UserSettingsStore,
        private config: AppConfigQuery,
        private http: HttpClient,
        private router: Router,
        private toastService: ToastService,
        private eventBus: SbEventBusService
    ) {}

    /** updateGlobalSettings - update global settings, (settings stored for global use), note this only updates settings locally
     * @param settings - settings value to update
     * @returns - observable with new global settings state
     */
    updateGlobalSettings(settings: Partial<GlobalValues>) {
        const { ui, loading, error, ...currentDomainState } =
            this.userSettingsStore.getValue();

        const newGlobalState = Object.entries(settings).reduce(
            (acc, [key, value]) => {
                acc[key] = { value, version: DEFAULT_SETTINGS[key].version };
                return acc;
            },
            { ...currentDomainState }
        );

        const newUiState: GlobalUIState = Object.keys(settings).reduce(
            (acc, key: GlobalSettingsKey) => {
                acc[key] = { status: 'PENDING' };
                return acc;
            },
            {} as GlobalUIState
        );
        this.userSettingsStore.update((state) => ({
            ...state,
            ...newGlobalState,
            ui: {
                ...state.ui,
                ...newUiState
            }
        }));
        return of(newGlobalState);
    }

    /** resetToDefault - reset settings for passed scope to default by deleting it from backend
     * @param scope - scope to reset settings for
     * @returns - observable for http request
     */
    resetToDefault(scope: SettingsScope) {
        return this.http
            .delete(`${this.config.get().adminBaseUrl}/v1/user-config/${scope}`)
            .pipe(
                tap((_) => {
                    this.userSettingsStore.reset();
                })
            );
    }

    /** saveGlobalSetting - save global setting, (setting stored for global use)
     * @param key - name of settings key to save
     * @param value - value to save
     * @returns - observable of saved value
     */
    saveGlobalSetting<T, K extends GlobalSettingsKey = GlobalSettingsKey>(
        key: K,
        value: T
    ) {
        return this._save(key, value, 'GLOBAL');
    }

    /** saveRouteSetting - save route setting, (setting stored for specific route e.g. /private-equity)
     * @param key - name of settings key to save
     * @param value - value to save
     * @param routeId - id of route to save setting for
     * @returns - observable of saved value
     */
    saveRouteSetting<T, K extends RouteSettingsKey = RouteSettingsKey>(
        key: K,
        value: T,
        routeId: string
    ) {
        return this._save(key, value, 'ROUTE', routeId);
    }

    /** saveComponentSetting - save component setting, (setting stored for specific component)
     * @param key - name of settings key to save
     * @param value - value to save
     * @param componentId - id of component to save setting for
     * @param onlyIfChanged - only save if value is different from current store value (it might have changed in the database)
     * @returns - observable of saved value
     */
    saveComponentSetting<T, K extends ComponentSettingsKey = ComponentSettingsKey>(
        key: K,
        value: T,
        componentId: string,
        onlyIfChanged = false
    ) {
        const isLoading = this.userSettingsStore.getValue().loading;

        // if onlyIfChanged is true, and component setting is already loaded or key exists in store...
        if (
            onlyIfChanged &&
            (isLoading ||
                (!!this.userSettingsStore.COMPONENT.getValue().entities[key] &&
                    !!this.userSettingsStore.COMPONENT.getValue().entities[key][
                        componentId
                    ]?.value))
        ) {
            // ...get current value from store
            const currentValue = this.userSettingsStore.COMPONENT.getValue().entities[key]
                ? this.userSettingsStore.COMPONENT.getValue().entities[key][componentId]
                      .value ?? null
                : null;
            // if current value is null and settings are still loading...
            if (currentValue === null && isLoading) {
                // ...return observable of passed value
                return of(value);
            } else if (deepEquals(currentValue, value)) {
                // if current value is equal to passed value, return observable of current value
                return of(currentValue);
            }
        }
        return this._save(key, value, 'COMPONENT', componentId);
    }

    private _save<T, K extends GlobalSettingsKey = GlobalSettingsKey>(
        key: K,
        value: T,
        scope: 'GLOBAL'
    ): Observable<SettingsValue<K>>;
    private _save<T, K extends ComponentSettingsKey = ComponentSettingsKey>(
        key: K,
        value: T,
        scope: 'COMPONENT',
        id: string
    ): Observable<SettingsValue<K>>;
    private _save<T, K extends RouteSettingsKey = RouteSettingsKey>(
        key: K,
        value: T,
        scope: 'ROUTE',
        route?: string
    ): Observable<SettingsValue<K>>;
    private _save<T>(key: SettingsKey, value: T, scope: SettingsScope, ...args) {
        let id: string = args[0];
        const version = DEFAULT_SETTINGS[key].version;

        let newState = { value, version };

        // if scope is ROUTE and no route is provided...
        if (scope === 'ROUTE' && !id) {
            // ...get current route (excluding query params and auxiliary routes)
            id = this.router.routerState.snapshot.url.match(/(?!^\/).[^?(]*/)[0];
        }

        // if scope is GLOBAL...
        if (scope === 'GLOBAL') {
            applyTransaction(() => {
                // ...update global store
                this.userSettingsStore.update((state) => ({
                    ...state,
                    [key]: newState
                }));
                this.userSettingsStore.update((state) => {
                    const newStatus: SettingsUI = {
                        status: 'PENDING'
                    };
                    return {
                        ...state,
                        ui: {
                            ...state.ui,
                            [key as GlobalSettingsKey]: newStatus
                        }
                    };
                });
            });
        } else if (scope === 'ROUTE') {
            applyTransaction(() => {
                // ...otherwise upsert entity store for scope using key and id
                this.userSettingsStore[scope].upsert(key, (state) => ({
                    ...state,
                    [id]: newState
                }));
                this._updateUIState<RouteSettingsKey>(<RouteSettingsKey>key, scope, id);
            });
        } else if (scope === 'COMPONENT') {
            applyTransaction(() => {
                // ...otherwise upsert entity store for scope using key and id
                this.userSettingsStore[scope].upsert(key, (state) => ({
                    ...state,
                    [id]: newState
                }));
                this._updateUIState(<ComponentSettingsKey>key, scope, id);
            });
        }
        let state;
        switch (scope) {
            case 'GLOBAL':
                state = { ...this.userSettingsStore.getValue() };
                delete state.ui;
                delete state.loading;
                delete state.loading;
                break;
            case 'ROUTE':
            case 'COMPONENT':
                state = { ...this.userSettingsStore[scope].getValue() };
                delete state.error;
                delete state.loading;
                break;
        }

        return this.http
            .put(`${this.config.get().adminBaseUrl}/v1/user-config/${scope}`, state)
            .pipe(
                catchError((err) => {
                    // Temporary fix (hack) for 404 put error (backend should create new resource if not found, i.e. upsert, also it should follow RESTful conventions)
                    if (err.error.status === 404) {
                        return this.http.post(
                            `${this.config.get().adminBaseUrl}/v1/user-config`,
                            { configName: scope, config: state }
                        );
                    } else {
                        // something else went wrong
                        this.toastService.show({
                            intent: 'critical',
                            message: 'Setting could not be saved, please try again later.'
                        });
                        return throwError(err);
                    }
                }),
                tap((res) => {
                    // update pending state, if global scope...
                    if (scope === 'GLOBAL') {
                        // ...update ui key in global state store
                        this.userSettingsStore.update((state) => {
                            const newStatus: SettingsUI = {
                                status: 'SAVED'
                            };
                            return {
                                ui: {
                                    [key as GlobalSettingsKey]: newStatus
                                }
                            };
                        });
                    } else {
                        // ...otherwise update key in entity ui store for scope
                        this._updateUIState(
                            <RouteSettingsKey | ComponentSettingsKey>key,
                            scope,
                            id,
                            'SAVED'
                        );
                    }
                }),
                mapTo(state.value)
            );
    }
    getAll() {
        this.userSettingsStore.setLoading(true);
        return this.http
            .get<Array<{ configName: string; config: any }>>(
                `${this.config.get().adminBaseUrl}/v1/user-config`
            )
            .pipe(
                tap((res) => {
                    let snapshots = {
                        'user-settings-global': {},
                        'user-settings-route': {
                            entities: {},
                            ids: [],
                            loading: false,
                            error: null
                        },
                        'user-settings-component': {
                            entities: {},
                            ids: [],
                            loading: false,
                            error: null
                        }
                    };
                    res.forEach((item) => {
                        switch (item.configName) {
                            case 'GLOBAL':
                            case 'ROUTE':
                            case 'COMPONENT':
                                const snapshotName = `user-settings-${item.configName.toLowerCase()}`;
                                // add snapshot of config to snapshots object
                                snapshots = {
                                    ...snapshots,
                                    [snapshotName]: item.config
                                };
                                break;
                        }
                    });

                    // load all settings into store
                    snapshotManager.setStoresSnapshot(snapshots);
                    this.userSettingsStore.setLoading(false);
                })
            );
    }

    private _updateUIState<K extends ComponentSettingsKey = ComponentSettingsKey>(
        key: K,
        scope: SettingsScope,
        id?: string,
        status: SettingsUIStatus = 'PENDING'
    ): void {
        this.userSettingsStore[scope].ui.upsert(key, (state: SettingsUIState<K>) => {
            const newEntry: SettingsUIState[K] = {
                [id]: { status }
            };
            return {
                ...state,
                ...newEntry
            };
        });
    }

    // TODO: add support for route and component scope?
    saveMultiple<T = Settings>(settings: T, scope: SettingsScope) {
        let state;
        switch (scope) {
            case 'GLOBAL':
                state = settings;
                applyTransaction(() => {
                    this.userSettingsStore.setLoading(true);
                    this.userSettingsStore.setError(null);
                });
                break;
            case 'COMPONENT':
            case 'ROUTE':
                throw new Error('Not implemented');
            /*case 'ROUTE':
                state = snapshotManager.getStoresSnapshot(['user-settings-route']);
                break;
            case 'COMPONENT':
                state = snapshotManager.getStoresSnapshot(['user-settings-component']);
                break;*/
        }
        return this.http
            .put<GlobalSettings>(
                `${this.config.get().adminBaseUrl}/v1/user-config/${scope}`,
                state
            )
            .pipe(
                catchError((err) => {
                    // Temporary fix (hack) for 404 put error (backend should create new resource if not found, i.e. upsert, also it should follow RESTful conventions)
                    if (err.error.status === 404) {
                        return this.http.post(
                            `${this.config.get().adminBaseUrl}/v1/user-config`,
                            { configName: scope, config: settings }
                        );
                    } else {
                        // something else went wrong
                        if (scope === 'GLOBAL') {
                            applyTransaction(() => {
                                this.userSettingsStore.setLoading(false);
                                this.userSettingsStore.setError(err);
                            });
                        }
                        return throwError(err);
                    }
                }),
                tap((res) => {
                    // update pending state, if global scope...
                    if (scope === 'GLOBAL') {
                        const newUiState: GlobalUIState = Object.keys(settings).reduce(
                            (acc, key: GlobalSettingsKey) => {
                                acc[key] = { status: 'SAVED' };
                                return acc;
                            },
                            {} as GlobalUIState
                        );
                        applyTransaction(() => {
                            this.userSettingsStore.setLoading(false);
                            this.userSettingsStore.update((state) => ({
                                ...state,
                                ui: {
                                    ...state.ui,
                                    ...newUiState
                                }
                            }));
                        });
                    }
                }),
                mapTo(settings)
            );
    }
}
